import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Person } from '../types';
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  createColumnHelper,
  flexRender,
  SortingState,
  ColumnFiltersState,
} from '@tanstack/react-table';

// 소속 옵션 (App.tsx와 동일)
const DEPARTMENT_OPTIONS = [
  { value: 'pxd', label: 'pxd', color: 'rgba(128,128,128,0.3)' },
  { value: '프리랜서', label: '프리랜서', color: 'rgba(0,123,255,0.3)' },
  { value: '외주사', label: '외주사', color: 'rgba(0,123,255,0.3)' },
  { value: '계약직', label: '계약직', color: 'rgba(0,123,255,0.3)' },
  { value: '인턴', label: '인턴', color: 'rgba(255,153,0,0.3)' },
  { value: '레이블', label: '레이블', color: 'rgba(68,68,68,1)' },
];

function getColorByDepartment(department: string) {
  return DEPARTMENT_OPTIONS.find(opt => opt.value === department)?.color || DEPARTMENT_OPTIONS[0].color;
}

function getDepartmentColorClass(department: string) {
  if (department === 'pxd') return 'dept-bg-pxd';
  if (['프리랜서', '외주사', '계약직'].includes(department)) return 'dept-bg-ext';
  if (department === '인턴') return 'dept-bg-intern';
  if (department === '레이블') return 'dept-bg-label';
  return 'dept-bg-pxd';
}

interface PersonManagementPanelProps {
  people: Person[];
  onUpdatePerson: (person: Person) => void;
  onAddPerson: (person: Omit<Person, 'id'>) => void;
  onDeletePerson: (id: number) => void;
  open: boolean;
  onClose: () => void;
}

interface EditingPerson {
  id?: number;
  name: string;
  department: string;
  startDate?: string;
  endDate?: string;
  projectName?: string;
  isPlanned?: boolean;
  usageType?: string;
  isNew?: boolean;
}

// 편집 가능한 셀 컴포넌트들
const EditableCell = ({ value, onSave, type = 'text', options }: {
  value: any;
  onSave: (value: any) => void;
  type?: 'text' | 'date' | 'select' | 'textarea';
  options?: Array<{ value: string; label: string }>;
}) => {
  const [editing, setEditing] = useState(false);
  const [inputValue, setInputValue] = useState(value);

  // value prop이 변경될 때 inputValue도 업데이트
  useEffect(() => {
    setInputValue(value);
  }, [value]);

  const handleSave = () => {
    onSave(inputValue);
    setEditing(false);
  };

  const handleCancel = () => {
    setInputValue(value);
    setEditing(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && type !== 'textarea') {
      handleSave();
    } else if (e.key === 'Escape') {
      handleCancel();
    } else if (type === 'date' && (e.key === 'Delete' || e.key === 'Backspace')) {
      // 날짜 필드에서 Delete/Backspace로 한번에 지우기
      if (e.key === 'Delete') {
        e.preventDefault();
        setInputValue('');
      }
    } else if (type === 'textarea' && e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSave();
    }
  };

  const handleDateClear = () => {
    // 즉시 상태 업데이트 및 편집 모드 종료
    setInputValue('');
    onSave('');
    setEditing(false);
  };

  if (editing) {
    if (type === 'select') {
      return (
        <select
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onBlur={handleSave}
          onKeyDown={handleKeyDown}
          className="cell-input"
          autoFocus
        >
          {options?.map(opt => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>
      );
    }
    
    if (type === 'textarea') {
      return (
        <textarea
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onBlur={handleSave}
          onKeyDown={handleKeyDown}
          className="cell-input cell-textarea"
          rows={2}
          autoFocus
        />
      );
    }

    if (type === 'date') {
      return (
        <div 
          className="date-input-container"
          onMouseDown={(e) => e.stopPropagation()}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="date-input-wrapper">
            <input
              type={type}
              value={inputValue || ''}
              onChange={(e) => setInputValue(e.target.value)}
              onBlur={handleSave}
              onKeyDown={handleKeyDown}
              className="cell-input date-input-expanded"
              autoFocus
            />
            <i className="ri-calendar-line date-picker-icon"></i>
          </div>
          {inputValue && (
            <button
              type="button"
              className="date-clear-btn"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleDateClear();
              }}
              onMouseDown={(e) => {
                e.preventDefault();
                e.stopPropagation();
              }}
              onMouseUp={(e) => {
                e.preventDefault();
                e.stopPropagation();
              }}
            >
              <i className="ri-close-line"></i>
            </button>
          )}
        </div>
      );
    }

    return (
      <input
        type={type}
        value={inputValue}
        onChange={(e) => setInputValue(e.target.value)}
        onBlur={handleSave}
        onKeyDown={handleKeyDown}
        className="cell-input"
        autoFocus
      />
    );
  }

  return (
    <div className="cell-display" onClick={() => setEditing(true)}>
      {value || '-'}
    </div>
  );
};

const DepartmentCell = ({ 
  department, 
  onSave, 
  cellId, 
  isOpen, 
  onToggle,
  forceUpward = false
}: { 
  department: string; 
  onSave: (value: string) => void;
  cellId: string;
  isOpen: boolean;
  onToggle: (cellId: string) => void;
  forceUpward?: boolean;
}) => {
  const [dropdownDirection, setDropdownDirection] = useState<'up' | 'down'>('down');
  const cellRef = useRef<HTMLDivElement>(null);

  const handleSelect = (value: string) => {
    onSave(value);
    onToggle(''); // 드롭다운 닫기
  };

  const handleToggle = () => {
    if (!isOpen && cellRef.current) {
      const rect = cellRef.current.getBoundingClientRect();
      
      // 테이블 컨테이너 찾기
      const tableContainer = cellRef.current.closest('.sidebar-content');
      if (tableContainer) {
        const containerRect = tableContainer.getBoundingClientRect();
        const cellBottomInContainer = rect.bottom - containerRect.top;
        const containerHeight = containerRect.height;
        
        // 셀이 컨테이너 하단 200px 이내에 있으면 위쪽으로 표시
        const isNearBottom = cellBottomInContainer > containerHeight - 200;
        
        console.log('Cell position:', {
          cellBottomInContainer,
          containerHeight,
          isNearBottom,
          forceUpward
        });
        
        if (forceUpward || isNearBottom) {
          setDropdownDirection('up');
        } else {
          setDropdownDirection('down');
        }
      } else {
        // 기본 로직
        const windowHeight = window.innerHeight;
        const spaceBelow = windowHeight - rect.bottom;
        const spaceAbove = rect.top;
        
        setDropdownDirection(spaceBelow < 200 && spaceAbove > spaceBelow ? 'up' : 'down');
      }
    }
    onToggle(isOpen ? '' : cellId);
  };

  // 외부 클릭 시 드롭다운 닫기
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      if (!target.closest('.custom-dropdown')) {
        onToggle(''); // 모든 드롭다운 닫기
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, onToggle]);

  return (
    <div ref={cellRef} className="custom-dropdown">
      <div 
        className={`cell-display dept-cell ${getDepartmentColorClass(department)}`} 
        onClick={handleToggle}
      >
        <span>{department}</span>
      </div>
      
      {isOpen && (
        <div className={`dropdown-menu ${dropdownDirection === 'up' ? 'dropdown-up' : 'dropdown-down'}`}>
          {DEPARTMENT_OPTIONS.map(option => (
            <div
              key={option.value}
              className={`dropdown-item ${department === option.value ? 'selected' : ''}`}
              onClick={() => handleSelect(option.value)}
            >
              <span className={`dept-indicator ${getDepartmentColorClass(option.value)}`}></span>
              <span>{option.label}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const PersonManagementPanel: React.FC<PersonManagementPanelProps> = ({
  people,
  onUpdatePerson,
  onAddPerson,
  onDeletePerson,
  open,
  onClose,
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [excludePxd, setExcludePxd] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingPerson, setEditingPerson] = useState<EditingPerson | null>(null);
  const [sorting, setSorting] = useState<SortingState>([{ id: 'id', desc: true }]);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [openDropdownId, setOpenDropdownId] = useState<string | null>(null);

  // 모달 열릴 때 body 스크롤 방지
  useEffect(() => {
    if (open) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [open]);

  // 검색 및 필터링된 데이터
  const filteredPeople = useMemo(() => {
    return (people || []).filter(person => {
      // pxd 제외 필터
      if (excludePxd && person.department === 'pxd') {
        return false;
      }
      
      // 검색 필터
      return person.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        person.department.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (person.projectName || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (person.usageType || '').toLowerCase().includes(searchTerm.toLowerCase());
    });
  }, [people, excludePxd, searchTerm]);

  // 카운트용 데이터 분리
  const counts = useMemo(() => {
    // 총 인원: 예정이 false이고 소속이 '레이블'이 아닌 인원
    const totalCount = filteredPeople.filter(person => 
      person.department !== '레이블' && !person.isPlanned
    ).length;
    
    // 총 예정 인원: 예정이 true인 인원
    const plannedCount = filteredPeople.filter(person => 
      person.isPlanned
    ).length;
    
    return { totalCount, plannedCount };
  }, [filteredPeople]);

  // 삭제 핸들러
  const handleDeletePerson = useCallback((personId: number) => {
    if (window.confirm('정말 삭제하시겠습니까?')) {
      onDeletePerson(personId);
    }
  }, [onDeletePerson]);

  // 필드 업데이트
  const updatePersonField = useCallback((personId: number, field: string, value: any) => {
    const person = people.find(p => p.id === personId);
    if (person) {
      onUpdatePerson({ ...person, [field]: value });
    }
  }, [people, onUpdatePerson]);

  // 컬럼 정의
  const columnHelper = createColumnHelper<Person>();
  
  const columns = useMemo(() => [
    columnHelper.accessor('id', {
      header: 'ID',
      cell: ({ getValue }) => getValue(),
      size: 0,
      enableSorting: true,
      meta: { hidden: true }
    }),

    columnHelper.accessor('name', {
      header: '이름',
      cell: ({ getValue, row }) => (
        <EditableCell
          value={getValue()}
          onSave={(value) => updatePersonField(row.original.id, 'name', value)}
        />
      ),
    }),

    columnHelper.accessor('department', {
      header: '소속',
      cell: ({ getValue, row, table }) => {
        const cellId = `dept-${row.original.id}`;
        const allRows = table.getRowModel().rows;
        const currentRowIndex = allRows.findIndex(r => r.original.id === row.original.id);
        const totalRows = allRows.length;
        const isInLastSixRows = currentRowIndex >= Math.max(0, totalRows - 6);
        
        return (
          <DepartmentCell
            department={getValue()}
            onSave={(value) => updatePersonField(row.original.id, 'department', value)}
            cellId={cellId}
            isOpen={openDropdownId === cellId}
            onToggle={setOpenDropdownId}
            forceUpward={isInLastSixRows}
          />
        );
      },
    }),

    columnHelper.accessor('startDate', {
      header: '시작일',
      cell: ({ getValue, row }) => (
        <EditableCell
          value={getValue()}
          onSave={(value) => updatePersonField(row.original.id, 'startDate', value)}
          type="date"
        />
      ),
    }),

    columnHelper.accessor('endDate', {
      header: '종료일',
      cell: ({ getValue, row }) => (
        <EditableCell
          value={getValue()}
          onSave={(value) => updatePersonField(row.original.id, 'endDate', value)}
          type="date"
        />
      ),
    }),

    columnHelper.accessor('projectName', {
      header: '프로젝트명',
      cell: ({ getValue, row }) => (
        <EditableCell
          value={getValue()}
          onSave={(value) => updatePersonField(row.original.id, 'projectName', value)}
        />
      ),
    }),

    columnHelper.accessor('isPlanned', {
      header: '예정',
      cell: ({ getValue, row }) => (
        <div 
          className={`round-checkbox ${getValue() ? 'checked' : ''}`}
          onClick={() => updatePersonField(row.original.id, 'isPlanned', !getValue())}
        >
          {getValue() && <i className="ri-check-line"></i>}
        </div>
      ),
    }),

    columnHelper.accessor('usageType', {
      header: '비고',
      cell: ({ getValue, row }) => (
        <EditableCell
          value={getValue()}
          onSave={(value) => updatePersonField(row.original.id, 'usageType', value)}
        />
      ),
    }),

    columnHelper.display({
      id: 'actions',
      header: '',
      cell: ({ row }) => (
        <button
          onClick={() => handleDeletePerson(row.original.id)}
          className="delete-btn flex items-center justify-center"
        >
          <i className="ri-delete-bin-line"></i>
        </button>
      ),
    }),
  ], [updatePersonField, handleDeletePerson, openDropdownId]);

  // 테이블 인스턴스
  const table = useReactTable({
    data: filteredPeople,
    columns,
    state: { sorting, columnFilters },
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
  });

  // 새 인원 추가 핸들러
  const handleAddPerson = () => {
    const newPerson = {
      name: '새 인원',
      department: 'pxd',
      startDate: '',
      endDate: '',
      projectName: '',
      isPlanned: false,
      usageType: '',
      color: getColorByDepartment('pxd'),
    };
        onAddPerson(newPerson);
  };

  // 추가 폼 제출
  const handleSubmitPerson = () => {
    if (!editingPerson) return;

    if (editingPerson.isNew) {
      onAddPerson({
        name: editingPerson.name,
        department: editingPerson.department,
        startDate: editingPerson.startDate || '',
        endDate: editingPerson.endDate || '',
        projectName: editingPerson.projectName || '',
        isPlanned: editingPerson.isPlanned || false,
        usageType: editingPerson.usageType || '',
        color: getColorByDepartment(editingPerson.department),
      });
    }

    setEditingPerson(null);
    setShowAddForm(false);
  };
  
  if (!open) return null;

  return (
    <>
      {/* 배경 오버레이 - 스크롤 차단 */}
      <div 
        className="person-management-overlay"
        onClick={onClose}
      />
      
      {/* 사이드바 */}
      <div className="person-management-sidebar">
        {/* 헤더 */}
        <div className="sidebar-title">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <h2 className="text-2xl font-semibold text-gray-700">인원 관리</h2>
              <div className="flex items-center gap-3">
                <span className="text-sm text-gray-500">총 {counts.totalCount}명</span>
                <span className="text-sm text-blue-500">총 예정 {counts.plannedCount}명</span>
              </div>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <i className="ri-close-line text-xl"></i>
            </button>
          </div>
          <div className="mt-2">
            <span className="text-xs text-gray-500">테이블 편집 사항은 즉시 반영/저장됩니다.</span>
          </div>
        </div>

        {/* 도구 모음 */}
        <div className="sidebar-header">
          <div className="flex items-center gap-3">
            <input
              type="text"
              placeholder="검색..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input"
            />
            
            <div 
              className={`filter-chip ${excludePxd ? 'active' : ''}`}
              onClick={() => setExcludePxd(!excludePxd)}
            >
              <span>pxd 제외</span>
              <i className={`ri-check-line ${excludePxd ? 'checked' : 'unchecked'}`}></i>
            </div>
          </div>
          
          <button onClick={handleAddPerson} className="btn-base btn-primary flex items-center btn-add">
            <i className="ri-add-line mr-1"></i>
            인원 추가
          </button>
        </div>

        {/* 테이블 */}
        <div className="sidebar-content">
          <table className="w-full person-table">
            <thead className="table-header">
              {table.getHeaderGroups().map(headerGroup => (
                <tr key={headerGroup.id}>
                  {headerGroup.headers.map(header => (
                    <th
                      key={header.id}
                      className={header.column.getCanSort() ? 'cursor-pointer select-none' : ''}
                      onClick={header.column.getToggleSortingHandler()}
                    >
                      {header.isPlaceholder ? null : (
                        <div className="flex items-center">
                          {flexRender(header.column.columnDef.header, header.getContext())}
                          <span className="sort-indicator">
                            {header.column.getIsSorted() === 'asc' && (
                              <i className="ri-arrow-up-line"></i>
                            )}
                            {header.column.getIsSorted() === 'desc' && (
                              <i className="ri-arrow-down-line"></i>
                            )}
                          </span>
                        </div>
                      )}
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody className="bg-white">
              {table.getRowModel().rows.map((row, index) => {
                const totalRows = table.getRowModel().rows.length;
                const isInLastSixRows = index >= Math.max(0, totalRows - 6);
                
                return (
                  <tr 
                    key={row.id} 
                    className={`table-row ${isInLastSixRows ? 'last-six-rows' : ''}`}
                  >
                    {row.getVisibleCells().map(cell => (
                      <td
                        key={cell.id}
                        className="table-cell"
                      >
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </td>
                    ))}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* 추가 모달 */}
      {showAddForm && editingPerson && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-60">
          <div className="bg-white rounded-lg p-6 w-96">
            <h3 className="text-lg font-semibold mb-4">새 인원 추가</h3>
            
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium mb-1">이름</label>
                <input
                  type="text"
                  value={editingPerson.name}
                  onChange={(e) => setEditingPerson({...editingPerson, name: e.target.value})}
                  className="form-input"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-1">시작일</label>
                <input
                  type="date"
                  value={editingPerson.startDate || ''}
                  onChange={(e) => setEditingPerson({...editingPerson, startDate: e.target.value})}
                  className="form-input"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-1">소속</label>
                <select
                  value={editingPerson.department}
                  onChange={(e) => setEditingPerson({...editingPerson, department: e.target.value})}
                  className="form-input"
                >
                  {DEPARTMENT_OPTIONS.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-1">종료일</label>
                <input
                  type="date"
                  value={editingPerson.endDate || ''}
                  onChange={(e) => setEditingPerson({...editingPerson, endDate: e.target.value})}
                  className="form-input"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-1">프로젝트명</label>
                <input
                  type="text"
                  value={editingPerson.projectName || ''}
                  onChange={(e) => setEditingPerson({...editingPerson, projectName: e.target.value})}
                  className="form-input"
                />
              </div>
              
              <div>
                <label className="flex items-center gap-2">
                  <div 
                    className={`round-checkbox ${editingPerson.isPlanned ? 'checked' : ''}`}
                    onClick={() => setEditingPerson({...editingPerson, isPlanned: !editingPerson.isPlanned})}
                  >
                    {editingPerson.isPlanned && <i className="ri-check-line"></i>}
                  </div>
                  <span className="text-sm font-medium">예정</span>
                </label>
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-1">비고</label>
                <input
                  type="text"
                  value={editingPerson.usageType || ''}
                  onChange={(e) => setEditingPerson({...editingPerson, usageType: e.target.value})}
                  className="form-input"
                />
              </div>
            </div>
            
            <div className="flex justify-end flex-gap-2 mt-6">
              <button
                onClick={() => setShowAddForm(false)}
                className="btn-base btn-secondary flex items-center"
              >
                <i className="ri-close-line mr-1"></i>
                취소
              </button>
              <button
                onClick={handleSubmitPerson}
                className="btn-base btn-primary flex items-center"
              >
                <i className="ri-check-line mr-1"></i>
                추가
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default PersonManagementPanel;